import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"
const API_URL = import.meta.env.VITE_API_URL || ''

/* ─── Gallery Lightbox ────────────────────────────────────────────────────── */
function Gallery({ images }) {
  const [active, setActive] = useState(0)
  const [lightbox, setLightbox] = useState(false)
  const [lbIdx, setLbIdx] = useState(0)

  // Listen for Escape and Arrow keys when lightbox is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightbox(false)
      if (e.key === 'ArrowRight') setLbIdx(i => (i + 1) % images.length)
      if (e.key === 'ArrowLeft') setLbIdx(i => (i - 1 + images.length) % images.length)
    }
    if (lightbox) window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightbox, images.length])

  if (!images || images.length === 0) return null

  const mainImg = images[active]

  const openLightbox = (i) => { setLbIdx(i); setLightbox(true) }
  const closeLightbox = () => setLightbox(false)
  const lbPrev = (e) => { e.stopPropagation(); setLbIdx(i => (i - 1 + images.length) % images.length) }
  const lbNext = (e) => { e.stopPropagation(); setLbIdx(i => (i + 1) % images.length) }

  // Calculate the looping thumbnails (max 3)
  const numThumbnails = Math.min(3, images.length - 1)
  const thumbnails = []
  for (let i = 1; i <= numThumbnails; i++) {
    const idx = (active + i) % images.length
    thumbnails.push({ idx, img: images[idx] })
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%' }}>
        {/* Main large image */}
        <div
          style={{ flex: 1, minHeight: 0, position: 'relative', cursor: 'pointer', borderRadius: '14px', overflow: 'hidden' }}
          onClick={() => openLightbox(active)}
        >
          <img src={mainImg.url} alt={mainImg.label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,20,40,0)', transition: 'background 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,20,40,0.15)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,20,40,0)'}
          />
          <div style={{ position: 'absolute', bottom: '12px', right: '12px', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '12px', fontFamily: FONT, padding: '4px 10px', borderRadius: '6px' }}>
            ⛶ View Full
          </div>
        </div>

        {/* Thumbnails row below */}
        {thumbnails.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${thumbnails.length}, 1fr)`, gap: '8px', height: '120px' }}>
            {thumbnails.map(({ idx, img }, i) => (
              <div
                key={idx}
                onClick={() => setActive(idx)}
                style={{
                  borderRadius: '10px', overflow: 'hidden', cursor: 'pointer',
                  position: 'relative',
                }}
              >
                <img src={img.url} alt={img.label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                
                {/* "+X More" overlay on the last thumbnail if there are hidden images */}
                {i === thumbnails.length - 1 && images.length > numThumbnails + 1 && (
                  <div
                    onClick={e => { e.stopPropagation(); openLightbox(active) }}
                    style={{
                      position: 'absolute', inset: 0, background: 'rgba(0,20,40,0.65)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontFamily: FONT, fontWeight: 700, fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    +{images.length - (numThumbnails + 1)} More
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={closeLightbox}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.92)', display: 'flex',
            flexDirection: 'column',
          }}
        >
          <style>{`
            .hide-scroll::-webkit-scrollbar { display: none; }
            .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
          `}</style>
          <button onClick={closeLightbox} style={{ position: 'absolute', top: '24px', right: '24px', background: 'none', border: 'none', color: '#fff', fontSize: '32px', cursor: 'pointer', lineHeight: 1, zIndex: 10 }}>✕</button>
          
          <button onClick={lbPrev} style={{ position: 'absolute', top: '50%', left: '24px', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', fontSize: '28px', cursor: 'pointer', padding: '12px 18px', borderRadius: '10px' }}>‹</button>
          <button onClick={lbNext} style={{ position: 'absolute', top: '50%', right: '24px', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', fontSize: '28px', cursor: 'pointer', padding: '12px 18px', borderRadius: '10px' }}>›</button>
          
          {/* Main Image Area - Flexible Center */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, padding: '40px 80px' }}>
            <img
              src={images[lbIdx]?.url}
              alt={images[lbIdx]?.label}
              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
            />
          </div>
            
          {/* Fixed Bottom Thumbnails Area */}
          <div style={{ height: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', paddingBottom: '24px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <div className="hide-scroll" style={{ display: 'flex', gap: '8px', overflowX: 'auto', overflowY: 'hidden', maxWidth: '90vw', paddingBottom: '4px' }}>
              {images.map((img, i) => (
                <img 
                  key={i} 
                  src={img.url} 
                  onClick={(e) => { e.stopPropagation(); setLbIdx(i); }}
                  style={{ 
                    height: '60px', width: '90px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', flexShrink: 0,
                    border: lbIdx === i ? `2px solid ${GOLD}` : '2px solid transparent',
                    opacity: lbIdx === i ? 1 : 0.6,
                    transition: 'all 0.2s'
                  }} 
                />
              ))}
            </div>

            <div style={{ color: 'rgba(255,255,255,0.6)', fontFamily: FONT, fontSize: '13px' }}>
              {lbIdx + 1} / {images.length} — {images[lbIdx]?.label}
            </div>
          </div>

        </div>
      )}
    </>
  )
}

/* ─── Amenities Grid ──────────────────────────────────────────────────────── */
function AmenitiesGrid({ amenities }) {
  if (!amenities || amenities.length === 0) return null
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
      {amenities.map((a, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: '#f4f7fb', borderRadius: '10px', padding: '12px 14px',
          border: '1px solid rgba(0,46,83,0.06)', transition: 'all 0.2s',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = `${GOLD}10`; e.currentTarget.style.borderColor = `${GOLD}30` }}
          onMouseLeave={e => { e.currentTarget.style.background = '#f4f7fb'; e.currentTarget.style.borderColor = 'rgba(0,46,83,0.06)' }}
        >
          <span style={{ color: GOLD, fontSize: '14px', flexShrink: 0 }}>✦</span>
          <span style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>{a.name}</span>
        </div>
      ))}
    </div>
  )
}

/* ─── Section Wrapper ─────────────────────────────────────────────────────── */
function Section({ title, badge, children, bg = '#fff', id }) {
  return (
    <div id={id} style={{ background: bg, padding: '64px 20px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {(title || badge) && (
          <div style={{ marginBottom: '36px' }}>
            {badge && (
              <span style={{
                display: 'inline-block', background: `${GOLD}18`, color: GOLD,
                fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
                padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
                border: `1px solid ${GOLD}35`, marginBottom: '10px',
              }}>{badge}</span>
            )}
            {title && (
              <h2 style={{ fontFamily: FONT, fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 700, color: NAVY, margin: 0 }}>
                {title}
              </h2>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

/* ─── Skeleton ────────────────────────────────────────────────────────────── */
function Skeleton() {
  const shim = { animation: 'shim 1.5s infinite', background: 'linear-gradient(90deg,#f0f4f8 25%,#e2e8f0 50%,#f0f4f8 75%)', backgroundSize: '200% 100%' }
  return (
    <div style={{ maxWidth: '1200px', margin: '100px auto 60px', padding: '0 20px', fontFamily: FONT }}>
      <style>{`@keyframes shim{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '48px' }}>
        <div style={{ ...shim, borderRadius: '14px', height: '380px' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[0, 1, 2, 3].map(i => <div key={i} style={{ ...shim, borderRadius: '10px', flex: 1 }} />)}
        </div>
      </div>
      {[200, 120, 80].map((h, i) => <div key={i} style={{ ...shim, borderRadius: '10px', height: `${h}px`, marginBottom: '20px' }} />)}
    </div>
  )
}

/* ─── Stat Tile ───────────────────────────────────────────────────────────── */
function StatTile({ icon, label, val }) {
  if (!val) return null
  return (
    <div style={{ background: '#f4f7fb', borderRadius: '12px', padding: '16px' }}>
      <div style={{ fontFamily: FONT, fontSize: '18px', marginBottom: '6px' }}>{icon}</div>
      <div style={{ fontFamily: FONT, fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>{label}</div>
      <div style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 700, color: NAVY }}>{val}</div>
    </div>
  )
}

/* ─── Main Page ───────────────────────────────────────────────────────────── */
export default function PropertyDetailPage() {
  const { slug, id } = useParams()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [property, setProperty] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sticky, setSticky] = useState(false)

  // Sticky bar after 400px scroll
  useEffect(() => {
    const onScroll = () => setSticky(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Fetch property detail
  useEffect(() => {
    if (!id) return
    setLoading(true)
    setError(null)
    fetch(`${API_URL}/api/properties/${id}`)
      .then(res => {
        if (res.status === 404) throw new Error('not_found')
        if (!res.ok) throw new Error(`Server error ${res.status}`)
        return res.json()
      })
      .then(data => { setProperty(data); setLoading(false) })
      .catch(err => { setError(err.message === 'not_found' ? 'not_found' : 'error'); setLoading(false) })
  }, [id])

  if (loading) return (
    <div style={{ fontFamily: FONT }}><Navbar /><Skeleton /><Footer /></div>
  )

  if (error === 'not_found') return (
    <div style={{ fontFamily: FONT }}>
      <Navbar />
      <div style={{ maxWidth: '600px', margin: '140px auto', padding: '0 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>🏚️</div>
        <h2 style={{ fontFamily: FONT, fontWeight: 700, color: NAVY, marginBottom: '10px' }}>Property Not Found</h2>
        <p style={{ color: '#6b7a8d', fontFamily: FONT, marginBottom: '28px' }}>This listing may no longer be available or the link may be incorrect.</p>
        <button onClick={() => navigate(-1)} style={{ background: GOLD, color: '#fff', border: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '14px', padding: '13px 32px', borderRadius: '10px', cursor: 'pointer' }}>
          ← Go Back
        </button>
      </div>
      <Footer />
    </div>
  )

  if (error) return (
    <div style={{ fontFamily: FONT }}>
      <Navbar />
      <div style={{ maxWidth: '600px', margin: '140px auto', padding: '0 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>⚠️</div>
        <h2 style={{ fontFamily: FONT, fontWeight: 700, color: NAVY, marginBottom: '10px' }}>Failed to Load Property</h2>
        <p style={{ color: '#6b7a8d', fontFamily: FONT, marginBottom: '28px' }}>There was a problem fetching this property's details. Please try again shortly.</p>
        <button onClick={() => window.location.reload()} style={{ background: GOLD, color: '#fff', border: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '14px', padding: '13px 32px', borderRadius: '10px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
      <Footer />
    </div>
  )

  const p = property

  // WhatsApp link
  const waNumber = p.contact?.whatsapp || p.contact?.phone || '971585815757'
  const cleanNumber = waNumber.replace(/[^0-9+]/g, '')
  const waText = encodeURIComponent(`Hi, I'm interested in the property "${p.name}"${p.area ? ` in ${p.area}` : ''}. Could you please share more details?`)
  const waLink = `https://wa.me/${cleanNumber}?text=${waText}`

  // Back route based on category
  const backHref = p.category === 'rent' ? '/properties/rent' : '/properties/buy'
  const backLabel = p.category === 'rent' ? 'For Rent' : 'For Sale'

  return (
    <div style={{ fontFamily: FONT }}>
      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @media (min-width: 901px) {
          .mobile-sticky-bar { display: none !important; }
        }
        @media (max-width: 900px) {
          .pd-hero-grid { grid-template-columns: 1fr !important; }
          .pd-about-grid { grid-template-columns: 1fr !important; }
          .pd-stats-row { grid-template-columns: repeat(2,1fr) !important; }
          html body .wa-float-btn, html body .wa-tooltip { bottom: 90px !important; }
          html body #ai-chatbot-toggle { bottom: 160px !important; }
          html body .chat-panel { bottom: 230px !important; }
        }
        @media (max-width: 520px) {
          .pd-stats-row { grid-template-columns: 1fr !important; }
          .pd-gallery-main { min-height: 200px !important; }
        }
      `}</style>

      <Navbar />

      {/* ══ HERO: GALLERY + SUMMARY ══════════════════════════════════════════ */}
      <div style={{ background: '#f4f7fb', paddingTop: '90px', paddingBottom: '64px', boxSizing: 'border-box' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px 0', boxSizing: 'border-box' }}>

          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', fontFamily: FONT, fontSize: '13px', color: '#6b7a8d', flexWrap: 'wrap' }}>
            <a href={backHref} style={{ color: GOLD, textDecoration: 'none', fontWeight: 600 }}>{backLabel}</a>
            <span>›</span>
            <span style={{ color: NAVY, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '280px' }}>{p.name}</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '32px', alignItems: 'stretch', animation: 'fadeIn 0.4s ease' }} className="pd-hero-grid">

            {/* Gallery */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {p.gallery && p.gallery.length > 0 ? (
                <div style={{ flex: 1, minHeight: 0 }}>
                  <Gallery images={p.gallery} />
                </div>
              ) : (
                <div className="pd-gallery-main" style={{ flex: 1, borderRadius: '14px', background: `linear-gradient(135deg, ${NAVY} 0%, #0a2d4a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '64px' }}>🏙️</span>
                </div>
              )}
            </div>

            {/* Summary sidebar card */}
            <div style={{ display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: '18px', padding: '28px', boxShadow: '0 8px 36px rgba(0,46,83,0.10)', border: '1px solid rgba(0,46,83,0.06)', position: 'sticky', top: '120px', height: '100%', boxSizing: 'border-box' }}>

              {/* Status + category badges */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <span style={{
                  display: 'inline-block', background: `${p.statusColor}18`, color: p.statusColor,
                  fontFamily: FONT, fontSize: '11px', fontWeight: 700, padding: '4px 12px',
                  borderRadius: '50px', letterSpacing: '0.5px', border: `1px solid ${p.statusColor}30`,
                }}>
                  {p.status}
                </span>
              </div>

              <h1 style={{ fontFamily: FONT, fontSize: 'clamp(17px, 2.2vw, 22px)', fontWeight: 800, color: NAVY, margin: '0 0 8px', lineHeight: 1.25 }}>
                {p.name}
              </h1>

              <div style={{ fontFamily: FONT, fontSize: '13px', color: '#6b7a8d', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px' }}>
                <span>📍</span>{p.area}
              </div>

              {/* Price */}
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid rgba(0,46,83,0.07)' }}>
                <div style={{ fontFamily: FONT, fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                  {p.category === 'rent' ? 'Annual Rent' : 'Asking Price'}
                </div>
                <div style={{ fontFamily: FONT, fontSize: '28px', fontWeight: 800, color: GOLD, lineHeight: 1 }}>
                  {p.price}
                </div>
              </div>

              {/* Key facts */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '11px', marginBottom: '22px' }}>
                {[
                  { icon: '🏠', label: 'Type', val: p.type },
                  { icon: '🛏', label: 'Bedrooms', val: p.beds },
                  { icon: '🚿', label: 'Bathrooms', val: p.baths },
                  { icon: '📐', label: 'Area', val: p.sqft },
                  { icon: '🛍️', label: 'Retail Space', val: p.retailSpace },
                  { icon: '🛋', label: 'Furnishing', val: p.furnishing },
                  { icon: '📜', label: 'Permit Number', val: p.permitNumber },
                ].filter(r => r.val).map((row, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: FONT, fontSize: '12px', color: '#6b7a8d', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {row.icon} {row.label}
                    </span>
                    <span style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY }}>{row.val}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'auto' }}>
                <a
                  href="/contact"
                  id="prop-detail-enquire"
                  style={{
                    display: 'block', textAlign: 'center',
                    background: NAVY, color: '#fff',
                    fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                    padding: '14px', borderRadius: '10px',
                    textDecoration: 'none', transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#00406e'}
                  onMouseLeave={e => e.currentTarget.style.background = NAVY}
                >
                  Enquire Now
                </a>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  id="prop-detail-whatsapp"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    background: GOLD, color: '#fff',
                    fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                    padding: '13px', borderRadius: '10px',
                    textDecoration: 'none', transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#d4af6a'}
                  onMouseLeave={e => e.currentTarget.style.background = GOLD}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                  WhatsApp Us
                </a>
                {p.youtubeLink && (
                  <a
                    href={p.youtubeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      background: '#ef4444', color: '#fff',
                      fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                      padding: '13px', borderRadius: '10px',
                      textDecoration: 'none', transition: 'background 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#dc2626'}
                    onMouseLeave={e => e.currentTarget.style.background = '#ef4444'}
                  >
                    ▶ {t('offplanExtra.watchVideo', 'Watch Video')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ ABOUT ════════════════════════════════════════════════════════════ */}
      <Section id="about">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'start' }} className="pd-about-grid">
          <div>
            <div style={{ marginBottom: '24px' }}>
              <span style={{
                display: 'inline-block', background: `${GOLD}18`, color: GOLD,
                fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
                padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
                border: `1px solid ${GOLD}35`, marginBottom: '10px',
              }}>Overview</span>
              <h2 style={{ fontFamily: FONT, fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 700, color: NAVY, margin: 0 }}>
                About This Property
              </h2>
            </div>
            {p.description ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {p.description.split('\n').filter(t => t.trim() !== '').map((text, idx) => (
                  <p key={idx} style={{ fontFamily: FONT, fontSize: '15px', color: '#4a5568', lineHeight: 1.8, margin: 0 }}>
                    {text}
                  </p>
                ))}
              </div>
            ) : (
              <p style={{ fontFamily: FONT, fontSize: '15px', color: '#9ca3af', lineHeight: 1.8, margin: 0, fontStyle: 'italic' }}>
                Contact our team for a full property briefing, including detailed descriptions, viewing schedules, and tailored investment analysis.
              </p>
            )}

          </div>
          {/* Quick stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '14px' }} className="pd-stats-row">
            <StatTile icon="📍" label="Location" val={p.area} />
            <StatTile icon="🏠" label="Type" val={p.type} />
            <StatTile icon="💰" label="Price" val={p.price} />
            <StatTile icon="📐" label="Size" val={p.sqft} />
            <StatTile icon="🛍️" label="Retail Space" val={p.retailSpace} />
            <StatTile icon="🛏" label="Bedrooms" val={p.beds} />
            <StatTile icon="🚿" label="Bathrooms" val={p.baths} />
            <StatTile icon="🛋" label="Furnishing" val={p.furnishing} />
            <StatTile icon="✨" label="Status" val={p.status} />
          </div>
        </div>
      </Section>

      {/* ══ AMENITIES ════════════════════════════════════════════════════════ */}
      {p.amenities && p.amenities.length > 0 && (
        <Section badge="Features & Amenities" title="What's Included" bg="#f4f7fb" id="amenities">
          <AmenitiesGrid amenities={p.amenities} />
        </Section>
      )}

      {/* ══ CONTACT CTA ══════════════════════════════════════════════════════ */}
      <Section id="contact">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }} className="pd-about-grid">
          {/* Contact info */}
          <div>
            <div style={{ marginBottom: '24px' }}>
              <span style={{
                display: 'inline-block', background: `${GOLD}18`, color: GOLD,
                fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
                padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
                border: `1px solid ${GOLD}35`, marginBottom: '10px',
              }}>Get in Touch</span>
              <h2 style={{ fontFamily: FONT, fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 700, color: NAVY, margin: 0 }}>
                Interested in This Property?
              </h2>
            </div>
            <p style={{ fontFamily: FONT, fontSize: '15px', color: '#4a5568', lineHeight: 1.8, margin: '0 0 28px' }}>
              Our specialist advisors are ready to arrange a viewing, answer all your questions, and guide you through the entire buying or renting process — with zero commission on selected properties.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <a href="/contact" style={{
                display: 'inline-block', background: NAVY, color: '#fff',
                fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                padding: '13px 28px', borderRadius: '10px', textDecoration: 'none',
                transition: 'background 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#00406e'}
                onMouseLeave={e => e.currentTarget.style.background = NAVY}
              >
                Contact an Agent
              </a>
              <a href={waLink} target="_blank" rel="noopener noreferrer" style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                background: '#25D366', color: '#fff',
                fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                padding: '13px 24px', borderRadius: '10px', textDecoration: 'none',
                transition: 'opacity 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                WhatsApp
              </a>
            </div>
          </div>

          {/* Quick info card */}
          <div style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`,
            borderRadius: '18px', padding: '32px', color: '#fff',
          }}>
            <div style={{ fontFamily: FONT, fontSize: '13px', color: GOLD, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '16px' }}>
              🏆 Why Victory Homes?
            </div>
            <ul style={{ fontFamily: FONT, fontSize: '14px', color: 'rgba(255,255,255,0.82)', lineHeight: 2.1, paddingLeft: '18px', margin: 0 }}>
              <li>Expert guidance from licensed UAE agents</li>
              <li>Access to exclusive off-market listings</li>
              <li>Transparent, no-hidden-fee process</li>
              <li>End-to-end support from search to handover</li>
            </ul>
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', fontFamily: FONT, fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              📞 <a href="tel:+971585815757" style={{ color: GOLD, textDecoration: 'none', fontWeight: 600 }}>+971 50 376 8662</a>
            </div>
          </div>
        </div>
      </Section>

      {/* ══ BOTTOM CTA ═══════════════════════════════════════════════════════ */}
      <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`, padding: '64px 20px', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <span style={{
            display: 'inline-block', background: `${GOLD}25`, color: GOLD,
            fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
            padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
            border: `1px solid ${GOLD}40`, marginBottom: '16px',
          }}>
            {p.category === 'rent' ? 'Ready to Rent?' : 'Ready to Buy?'}
          </span>
          <h2 style={{ fontFamily: FONT, fontWeight: 800, fontSize: 'clamp(22px, 3vw, 34px)', color: '#fff', margin: '0 0 12px', lineHeight: 1.2 }}>
            {p.category === 'rent'
              ? 'Schedule a Viewing Today'
              : 'Secure This Property Before It\'s Gone'}
          </h2>
          <p style={{ fontFamily: FONT, fontSize: '15px', color: 'rgba(255,255,255,0.7)', margin: '0 0 32px', lineHeight: 1.7 }}>
            Our advisors are available 7 days a week to help you move fast on the right property — with full transparency and zero pressure.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <a href="/contact" style={{
              background: GOLD, color: '#fff', fontFamily: FONT, fontWeight: 700,
              fontSize: '15px', padding: '15px 36px', borderRadius: '10px', textDecoration: 'none',
              transition: 'background 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#d4af6a'}
              onMouseLeave={e => e.currentTarget.style.background = GOLD}
            >
              Get in Touch
            </a>
            <a href={waLink} target="_blank" rel="noopener noreferrer" style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', fontFamily: FONT, fontWeight: 600, fontSize: '15px',
              padding: '15px 36px', borderRadius: '10px', textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            >
              💬 WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* ══ STICKY MOBILE BAR ════════════════════════════════════════════════ */}
      {sticky && (
        <div className="mobile-sticky-bar" style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
          background: '#fff', padding: '14px 20px',
          boxShadow: '0 -4px 24px rgba(0,46,83,0.14)',
          display: 'flex', gap: '10px', alignItems: 'center',
          borderTop: `3px solid ${GOLD}`,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
            <div style={{ fontFamily: FONT, fontSize: '12px', color: GOLD, fontWeight: 600 }}>{p.price}</div>
          </div>
          <a href="/contact" style={{ background: NAVY, color: '#fff', fontFamily: FONT, fontWeight: 700, fontSize: '13px', padding: '10px 20px', borderRadius: '8px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Enquire
          </a>
          <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ background: '#25D366', color: '#fff', fontFamily: FONT, fontWeight: 700, fontSize: '13px', padding: '10px 16px', borderRadius: '8px', textDecoration: 'none' }}>
            💬
          </a>
        </div>
      )}

      <Footer />
    </div>
  )
}
